import { createReducer, on } from '@ngrx/store';
import { CopilotActions } from './copilot.actions';
import { Conversation, Message } from '../models/copilot.models';

export interface CopilotClientState {
  conversations: Conversation[];
  messages: { [conversationId: string]: Message[] };
  selectedConversationId: string | null;
  lastActiveConversationId?: string;
  lastArchivedConversationId?: string;
  isStreaming: { [conversationId: string]: boolean };
  inputTextByConversation: { [conversationId: string]: string };
}

export interface CopilotState {
  clients: {
    [clientId: string]: CopilotClientState;
  };
  selectedClientId: string | null;
  highlightedClientIds: Set<string>;
  highlightedConversations: Set<string>;

}

const initialState: CopilotState = {
  clients: {},
  selectedClientId: null,
  highlightedClientIds: new Set(),
  highlightedConversations: new Set()
};

export const copilotReducer = createReducer(
  initialState,

  on(CopilotActions.selectClient, (state, { clientId }) => ({
    ...state,
    selectedClientId: clientId
  })),
  //
  // on(CopilotActions.selectConversation, (state, { clientId, conversationId }) => ({
  //   ...state,
  //   clients: {
  //     ...state.clients,
  //     [clientId]: {
  //       ...state.clients[clientId],
  //       selectedConversationId: conversationId
  //     }
  //   }
  // })),

  on(CopilotActions.selectConversation, (state, { clientId, conversationId, view }) => {
    const client = state.clients[clientId];

    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          selectedConversationId: conversationId,
          ...(view === 'active' && { lastActiveConversationId: conversationId }),
          ...(view === 'archived' && { lastArchivedConversationId: conversationId }),
        }
      }
    };
  }),


on(CopilotActions.loadConversationsSuccess, (state, { clientId, conversations }) => ({
    ...state,
    clients: {
      ...state.clients,
      [clientId]: {
        conversations,
        messages: {},
        selectedConversationId: null,
        isStreaming: {},
        inputTextByConversation: {}
      }
    }
  })),

  on(CopilotActions.loadMessagesSuccess, (state, { clientId, conversationId, messages }) => {
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...state.clients[clientId],
          messages: {
            ...state.clients[clientId]?.messages,
            [conversationId]: messages
          }
        }
      }
    };
  }),

  on(CopilotActions.messageReceived, (state, { clientId, conversationId, message }) => {
    const oldMessages = state.clients[clientId]?.messages[conversationId] || [];
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...state.clients[clientId],
          messages: {
            ...state.clients[clientId]?.messages,
            [conversationId]: [...oldMessages, message]
          }
        }
      }
    };
  }),

  on(CopilotActions.startStreaming, (state, { clientId, conversationId }) => ({
    ...state,
    clients: {
      ...state.clients,
      [clientId]: {
        ...state.clients[clientId],
        isStreaming: {
          ...state.clients[clientId]?.isStreaming,
          [conversationId]: true
        }
      }
    }
  })),

  on(CopilotActions.stopStreaming, (state, { clientId, conversationId }) => ({
    ...state,
    clients: {
      ...state.clients,
      [clientId]: {
        ...state.clients[clientId],
        isStreaming: {
          ...state.clients[clientId]?.isStreaming,
          [conversationId]: false
        }
      }
    }
  })),

  on(CopilotActions.triggerClientHighlight, (state, { clientId }) => {
    const next = new Set(state.highlightedClientIds);
    next.add(clientId);

    return {
      ...state,
      highlightedClientIds: next
    };
  }),

  on(CopilotActions.clearClientHighlight, (state, { clientId }) => {
    const next = new Set(state.highlightedClientIds);
    next.delete(clientId);

    return {
      ...state,
      highlightedClientIds: next
    };
  }),
  on(CopilotActions.clearClient, (state, { clientId }) => {
    const updatedClients = { ...state.clients };
    delete updatedClients[clientId];

    const updatedHighlighted = new Set(state.highlightedClientIds);
    updatedHighlighted.delete(clientId);

    return {
      ...state,
      clients: updatedClients,
      highlightedClientIds: updatedHighlighted,
      selectedClientId: state.selectedClientId === clientId ? null : state.selectedClientId
    };
  }),
  on(CopilotActions.messageUpdated, (state, { clientId, conversationId, messageId, content, traces, metadata }) => {
    const messages = state.clients[clientId]?.messages[conversationId] || [];

    const updatedMessages = messages.map((msg) => {
      if (msg.id !== messageId) return msg;

      const newMetadata = {
        ...msg.metadata,
        ...metadata
      };

      const updatedMsg = {
        ...msg,
        content,
        metadata: newMetadata,
        ...(traces ? { traces } : {})
      };

      // Replace temp ID with final ID if finalized
      if (newMetadata.finalized && newMetadata['finalId']) {
        updatedMsg.id = newMetadata['finalId'];
      }

      return updatedMsg;
    });

    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...state.clients[clientId],
          messages: {
            ...state.clients[clientId].messages,
            [conversationId]: updatedMessages
          }
        }
      }
    };
  }),
  on(CopilotActions.updateInputText, (state, { clientId, conversationId, text }) => ({
    ...state,
    clients: {
      ...state.clients,
      [clientId]: {
        ...state.clients[clientId],
        inputTextByConversation: {
          ...state.clients[clientId]?.inputTextByConversation,
          [conversationId]: text
        }
      }
    }
  })),

  on(CopilotActions.clearInputText, (state, { clientId, conversationId }) => {
    const { [conversationId]: _, ...rest } = state.clients[clientId]?.inputTextByConversation ?? {};
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...state.clients[clientId],
          inputTextByConversation: rest
        }
      }
    };
  }),

  on(CopilotActions.triggerConversationHighlight, (state, { clientId, conversationId }) => {
    const key = `${clientId}:${conversationId}`;
    const next = new Set(state.highlightedConversations);
    next.add(key);
    return {
      ...state,
      highlightedConversations: next
    };
  }),

  on(CopilotActions.clearConversationHighlight, (state, { clientId, conversationId }) => {
    const key = `${clientId}:${conversationId}`;
    const next = new Set(state.highlightedConversations);
    next.delete(key);
    return {
      ...state,
      highlightedConversations: next
    };
  }),
);

